<template>
  <Section
    :id="id"
    :header="t('lineItems.lineItems', 2)"
    class="line-items"
    data-class="flex flex-column flex-gap-1"
  >
    <Message v-if="stopFilter" severity="info" class="rygen-message" closable>
      <i18n-t keypath="lineItems.showingForStop" tag="span">
        <template #stop>
          {{
            stopFilter.location?.locationName ?? stopFilter.location?.cityState
          }}
        </template>

        <template #link>
          <router-link
            v-if="locationSectionId"
            :to="{ hash: `#${locationSectionId}`, query: queryParams }"
          >
            {{ t('multistop.selectDifferentStop') }}
          </router-link>
          <template v-else>{{ t('multistop.selectDifferentStop') }}</template>
        </template>
      </i18n-t>
    </Message>

    <LineItemSummaryCard :line-items="internalValue" />

    <LineItemViewEdit
      v-for="(lineItem, index) in lineItemsByStop"
      :key="index"
      :model-value="lineItem"
      :index="index"
      :handling-units="handlingUnits"
      :removable="internalValue.length > 1"
      :editable="editable"
      :show-stops="showStops"
      class="line-item"
      requires-confirmation
      @duplicate="onDuplicate"
      @remove="onRemove(index)"
      @update:model-value="onUpdate(index, $event)"
      @update:edit-mode="onEditExisting"
    />
    <LineItemViewEdit
      v-if="newLineItem"
      v-model="newLineItem"
      :index="internalValue.length"
      :handling-units="handlingUnits"
      :show-stops="showStops"
      editable
      edit-mode
      removable
      requires-confirmation
      class="line-item"
      @cancel="newLineItem = null"
      @done="onSubmit"
      @remove="newLineItem = null"
    />
    <p v-if="!internalValue.length">
      {{ t('common.noParam', { param: t('lineItems.lineItems', 2) }) }}
    </p>

    <InputError
      v-for="error of errors"
      :id="error.$uid"
      :key="error.$uid"
      :error="error"
    />

    <AddButton
      v-if="editable"
      class="align-self-start"
      :disabled="!!newLineItem"
      :label="t('lineItems.lineItems')"
      @click="onAdd"
    />
  </Section>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, inject, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useDirtyState } from '@rygen/rygen-ui/composables';
import { ReferenceNumberTypeLevel } from '@rygen/rygen-ui/model';

import { useReferenceNumberTypes } from '@/composables';
import {
  LineItem,
  Order,
  OrderLineItem,
  QuickQuoteLineItem,
  QuickQuoteRequest,
  Shipment,
  ShipmentLineItem,
  ShipmentStop,
} from '@/model';
import {
  ConsolidatedShipment,
  ConsolidatedShipmentLineItem,
} from '@/model/consolidation';
import { useHandlingUnitStore } from '@/stores/HandlingUnit';
import { useParentEntityStore } from '@/stores/ParentEntity';

import LineItemSummaryCard from '@/components/lineItem/LineItemSummaryCard.vue';
import LineItemViewEdit from '@/components/lineItem/LineItemViewEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Message from 'primevue/message';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  stopFilter: {
    type: ShipmentStop,
    default: () => undefined,
  },
  /** An array of vuelidate errors for the line items array as a whole. */
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  showStops: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, stopFilter, errors, editable, showStops } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'change', payload: { isDirty: boolean; numDirty: number }): void;
  (event: 'remove', lineItem: ShipmentLineItem): void;
  (event: 'update:model-value', newVal: LineItem[]): void;
}>();

const { handlingUnits, loadHandlingUnits } = useHandlingUnitStore();
const { parentEntity } = useParentEntityStore();
const { t } = useI18n();
const { owningClient } = useParentEntityStore();
// Make sure the line item refNum types are loaded before the individual line
// item components are mounted so we can calculate the default refNum types.
const { loadReferenceNumberTypes } = useReferenceNumberTypes();
const router = useRouter();

const internalValue: Ref<LineItem[]> = ref([...modelValue.value]);
const { dirtyState, itemAdded, itemDeleted, itemEdited, resetDirtyState } =
  useDirtyState(internalValue.value);

const newLineItem: Ref<LineItem | null> = ref(null);

const locationSectionId = inject('locationSectionId', false) as
  string | boolean;

const LineItemClazz = computed(() => {
  if (parentEntity.value instanceof Order) {
    return OrderLineItem;
  } else if (parentEntity.value instanceof Shipment) {
    return ShipmentLineItem;
  } else if (parentEntity.value instanceof ConsolidatedShipment) {
    return ConsolidatedShipmentLineItem;
  } else if (parentEntity.value instanceof QuickQuoteRequest) {
    return QuickQuoteLineItem;
  } else {
    console.warn(
      'LineItemSection can not find a parent entity. Defaulting to OrderLineItem',
    );
    return OrderLineItem;
  }
});

const queryParams = computed(() => router.currentRoute.value.query);

const lineItemsByStop = computed(() => {
  if (!stopFilter?.value) {
    return internalValue.value;
  }

  const id = stopFilter.value.id;
  return internalValue.value.filter((l) => {
    if (ShipmentLineItem.isShipmentLineItem(l)) {
      return (
        (l.pickupStopId && l.pickupStopId === id) ||
        (l.dropOffStopId && l.dropOffStopId === id)
      );
    }
  });
});

const loadStuffForEditting = () => {
  loadHandlingUnits();
};

const onAdd = async () => {
  loadStuffForEditting();
  newLineItem.value = new LineItemClazz.value();
};

const onRemove = debounce(
  (index: number) => {
    const [removedLineItem] = internalValue.value.splice(index, 1);
    const isSavedItem = !!removedLineItem.id;
    itemDeleted(index, isSavedItem);
    if (
      removedLineItem instanceof ShipmentLineItem &&
      removedLineItem.orderId
    ) {
      emit('remove', removedLineItem);
    }
  },
  300,
  { leading: true, trailing: false },
);

// TODO: This will work for a single stop but not for multistop
const onUpdate = (index: number, lineItem: LineItem) => {
  const [replacedLineItem] = internalValue.value.splice(index, 1, lineItem);
  const isUpdatedItemDirty = !isEqual(lineItem, replacedLineItem);
  itemEdited(index, isUpdatedItemDirty);
};

const onDuplicate = debounce(
  (lineItem: LineItem) => {
    const duplicate = new LineItemClazz.value({ ...lineItem, id: undefined });
    if (ShipmentLineItem.isShipmentLineItem(duplicate)) {
      duplicate.shipOrderId = undefined;
      duplicate.shipOrderLineItemId = undefined;
    }
    addLineItem(duplicate);
  },
  300,
  { leading: true, trailing: false },
);

const addLineItem = (lineItem: LineItem) => {
  internalValue.value.push(lineItem);
  itemAdded();
};

const onSubmit = (lineItem: LineItem) => {
  addLineItem(lineItem);
  newLineItem.value = null;
};

const onEditExisting = (editMode: boolean) => {
  if (editMode) {
    loadStuffForEditting();
  }
};

const reset = () => {
  nextTick(() => resetDirtyState(modelValue.value));
};

watch(
  modelValue,
  (newVal: LineItem[]) => {
    if (!isEqual(newVal, internalValue.value)) {
      resetDirtyState(newVal);
    }

    internalValue.value = newVal.map((l) => {
      const SubLineItem = LineItem.getSubclass(l);
      return new SubLineItem(l);
    });
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: LineItem[]) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
      emit('change', dirtyState.value);
    }
  },
  { deep: true },
);

watch(
  () => owningClient.value?.id,
  (newVal: number | undefined) => {
    if (newVal) {
      loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.LINE_ITEM });
    }
  },
  { immediate: true },
);

defineExpose({ reset });
</script>

<style>
.line-items {
  counter-reset: line-item-num;
}
</style>
